import React, { useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Icon,
  Input,
  Modal,
  Pills,
  Select,
  Text,
} from 'barte-design-system';
import {
  ArrowSquareOutIcon,
  CheckCircleIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { Terminal, TerminalStatus, VinculationEntry } from './POS';
import { STATUS_PILL, STATUS_TOOLTIP, OWNER_TOOLTIP } from './POS';
import './pos-shared.css';
import './DrawerDetalhesTerminal.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type ConfirmAction = 'sucesso' | 'falha' | 'manutencao' | 'mudarStatus';

interface Props {
  terminal: Terminal | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (
    terminalId: string,
    newStatus: TerminalStatus,
    extra?: { failureReason?: string; lastTestDate?: string },
  ) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TODAY = '14/04/2026';

// ─── Modal copy ───────────────────────────────────────────────────────────────

const CONFIRM_COPY = {
  sucesso: {
    title: 'Confirmar aprovação nos testes',
    description: (id: string) =>
      `Ao confirmar, o terminal ${id} será movido para Estoque Barte. Essa ação não pode ser desfeita.`,
    primaryLabel: 'Confirmar aprovação',
    newStatus: 'Estoque Barte' as TerminalStatus,
  },
  falha: {
    title: 'Registrar falha nos testes',
    description: (id: string) =>
      `Ao confirmar, o terminal ${id} será movido para Manutenção. Descreva o motivo da falha antes de continuar.`,
    primaryLabel: 'Confirmar falha',
    newStatus: 'Manutenção' as TerminalStatus,
  },
  manutencao: {
    title: 'Enviar para Manutenção',
    description: (id: string) =>
      `Ao confirmar, o terminal ${id} será movido para Manutenção. Descreva o motivo antes de continuar.`,
    primaryLabel: 'Confirmar',
    newStatus: 'Manutenção' as TerminalStatus,
  },
  mudarStatus: {
    title: 'Mudar status do terminal',
    description: (id: string) =>
      `Selecione o novo status para o terminal ${id}.`,
    primaryLabel: 'Confirmar',
    newStatus: 'Estoque Barte' as TerminalStatus, // sobrescrito em handleConfirm
  },
};

// ─── Sub-componente: tabela genérica de histórico ─────────────────────────────

function HistoryTable({ history }: { history: VinculationEntry[] | undefined }) {
  if (!history || history.length === 0) {
    return (
      <div className="ddt-history-empty">
        <Text variant="body-sm" className="ddt-label">Nenhum vínculo anterior registrado.</Text>
      </div>
    );
  }

  return (
    <div className="ddt-history-table-wrap">
      <table className="ddt-history-table">
        <thead>
          <tr>
            <th>Intermediador</th>
            <th>Lojista</th>
            <th>Última transação</th>
            <th>Data vinculado</th>
            <th>Data desvinculado</th>
          </tr>
        </thead>
        <tbody>
          {history.map((entry, i) => (
            <tr key={i}>
              <td>
                <div className="ddt-history-cell">
                  <span className="ddt-history-primary">{entry.payfac}</span>
                  <span className="ddt-history-secondary">{entry.payfacId}</span>
                </div>
              </td>
              <td>
                {entry.seller === '—' ? (
                  <span className="ddt-label">—</span>
                ) : (
                  <div className="ddt-history-cell">
                    <span className="ddt-history-primary">{entry.seller}</span>
                    <span className="ddt-history-secondary">{entry.sellerId}</span>
                  </div>
                )}
              </td>
              <td><span className="ddt-history-date">{entry.lastTransactionDate}</span></td>
              <td><span className="ddt-history-date">{entry.vinculationDate}</span></td>
              <td><span className="ddt-history-date">{entry.devinculationDate}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sub-componente: seção de checklist ──────────────────────────────────────

const CHECKLIST_URL = 'https://pos-checklist-testes.stefano-costa.workers.dev/';

function ChecklistSection({ onConfirm }: { onConfirm?: (action: 'sucesso' | 'falha') => void }) {
  return (
    <section className="ddt-section">
      <Text variant="label-sm" className="ddt-section-title">Checklist de testes</Text>
      <Text variant="body-sm" className="ddt-action-desc">
        Acesse o formulário e registre o resultado para este terminal.
      </Text>
      <Button
        variant="outline"
        size="sm"
        iconRight={<Icon icon={ArrowSquareOutIcon} size={16} weight="regular" />}
        className="ddt-link-btn"
        onClick={() => window.open(CHECKLIST_URL, '_blank', 'noopener,noreferrer')}
      >
        Abrir checklist de testes
      </Button>
      {onConfirm && (
        <div className="ddt-checklist-btns">
          <Button variant="fill" size="sm" iconLeft={<Icon icon={CheckCircleIcon} size={16} weight="regular" />} onClick={() => onConfirm('sucesso')}>
            Sucesso
          </Button>
          <Button variant="outline" size="sm" iconLeft={<Icon icon={WarningIcon} size={16} weight="regular" />} onClick={() => onConfirm('falha')}>
            Falha
          </Button>
        </div>
      )}
    </section>
  );
}

// ─── Sub-componente: grid de info ─────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="ddt-info-row">
      <Text variant="body-sm" className="ddt-label">{label}</Text>
      <div className="ddt-info-value">{value}</div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DrawerDetalhesTerminal({ terminal, isOpen, onClose, onStatusChange }: Props) {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [failureReason, setFailureReason] = useState('');
  const [targetStatus, setTargetStatus] = useState<TerminalStatus | ''>('');
  const [mudarStatusStep, setMudarStatusStep] = useState<1 | 2>(1);

  function handleConfirm() {
    if (!terminal || !confirmAction) return;
    let resolvedStatus: TerminalStatus;
    if (confirmAction === 'mudarStatus') {
      resolvedStatus = targetStatus as TerminalStatus;
    } else if (confirmAction === 'sucesso') {
      resolvedStatus = terminal.status === 'Teste' ? 'Estoque Barte' : terminal.status;
    } else {
      resolvedStatus = CONFIRM_COPY[confirmAction].newStatus;
    }
    onStatusChange(terminal.id, resolvedStatus, {
      lastTestDate: TODAY,
      ...(confirmAction === 'falha' ? { failureReason } : {}),
    });
    setConfirmAction(null);
    setFailureReason('');
    setTargetStatus('');
    setMudarStatusStep(1);
    onClose();
  }

  function handleCloseModal() {
    setConfirmAction(null);
    setFailureReason('');
    setTargetStatus('');
    setMudarStatusStep(1);
  }

  function handleOpenConfirm(action: ConfirmAction) {
    setFailureReason('');
    setMudarStatusStep(1);
    setConfirmAction(action);
  }

  function handleMudarStatusNext() {
    setMudarStatusStep(2);
  }

  function handleMudarStatusBack() {
    setMudarStatusStep(1);
  }

  if (!terminal) return null;

  const { status } = terminal;

  const activeSucessoCopy = status === 'Teste'
    ? CONFIRM_COPY.sucesso
    : {
        title: 'Confirmar sucesso no checklist',
        description: (id: string) =>
          `Ao confirmar, o resultado de sucesso será registrado. O terminal ${id} permanece em ${status}.`,
        primaryLabel: 'Confirmar',
      };

  const copy = confirmAction === null
    ? null
    : confirmAction === 'sucesso'
      ? activeSucessoCopy
      : CONFIRM_COPY[confirmAction];
  const canConfirm =
    confirmAction === 'sucesso' ||
    ((confirmAction === 'falha' || confirmAction === 'manutencao') && failureReason.trim().length > 0) ||
    (confirmAction === 'mudarStatus' && mudarStatusStep === 2);

  const drawerFooter =
    status === 'Teste' ? {
      primaryButton: {
        children: 'Sucesso',
        onClick: () => handleOpenConfirm('sucesso'),
        iconLeft: <Icon icon={CheckCircleIcon} size={16} weight="regular" />,
      },
      secondaryButton: {
        children: 'Falha',
        variant: 'outline' as const,
        onClick: () => handleOpenConfirm('falha'),
        iconLeft: <Icon icon={WarningIcon} size={16} weight="regular" />,
      },
    }
    : status === 'Manutenção' ? {
      primaryButton: {
        children: 'Mudar status',
        onClick: () => handleOpenConfirm('mudarStatus'),
      },
    }
    : undefined;

  return (
    <>
      <Drawer isOpen={isOpen} title={terminal.id} onClose={onClose} footer={drawerFooter}>
        <div className="ddt-body">

          {/* ═══════════════════════════════════════════════════════
              TESTE — alert no topo
          ══════════════════════════════════════════════════════════ */}
          {status === 'Teste' && (
            <Alert
              variant="info"
              title="Terminal bloqueado"
              description="Vinculado automaticamente ao Intermediador/ Lojista de Testes Barte. Nenhuma operação externa é permitida até aprovação no checklist."
              showCloseButton={false}
            />
          )}

          {/* ── Informações gerais — aparece em todos os status ─── */}
          <section className="ddt-section">
            <Text variant="label-sm" className="ddt-section-title">Informações sobre o terminal</Text>
            <div className="ddt-info-grid">
              <InfoRow label="Serial" value={<Text variant="label-sm">{terminal.serialNumber}</Text>} />
              <InfoRow
                label="Proprietário"
                value={
                  <div className="status-tooltip" data-tooltip={OWNER_TOOLTIP[terminal.owner]}>
                    <Pills
                      label={terminal.owner}
                      state="default"
                      variant={terminal.owner === 'Barte' ? 'fill' : 'light'}
                      size="sm"
                    />
                  </div>
                }
              />
              <InfoRow
                label="Status"
                value={
                  <div className="status-tooltip" data-tooltip={STATUS_TOOLTIP[terminal.status]}>
                    <Pills label={terminal.status} state={STATUS_PILL[terminal.status]} size="sm" />
                  </div>
                }
              />
              <InfoRow label="Versão POS" value={<Text variant="label-sm">{terminal.posVersion}</Text>} />
              <InfoRow label="ID Phoebus POS" value={<Text variant="label-sm">{terminal.terminalId}</Text>} />
              <InfoRow label="Data de cadastro" value={<Text variant="label-sm">{terminal.registrationDate}</Text>} />
              {terminal.lastTestDate && (
                <InfoRow label="Último teste" value={<Text variant="label-sm">{terminal.lastTestDate}</Text>} />
              )}
            </div>
          </section>

          {/* ═══════════════════════════════════════════════════════
              TESTE — checklist
          ══════════════════════════════════════════════════════════ */}
          {status === 'Teste' && <ChecklistSection />}

          {/* ═══════════════════════════════════════════════════════
              ESTOQUE BARTE — último teste + histórico de vínculos
          ══════════════════════════════════════════════════════════ */}
          {status === 'Estoque Barte' && (
            <>
              <section className="ddt-section">
                <Text variant="label-sm" className="ddt-section-title">Histórico de vínculos</Text>
                <HistoryTable history={terminal.vinculationHistory} />
              </section>

              <ChecklistSection onConfirm={handleOpenConfirm} />
            </>
          )}

          {/* ═══════════════════════════════════════════════════════
              ESTOQUE PAYFAC — payfac + datas
          ══════════════════════════════════════════════════════════ */}
          {status === 'Estoque Intermediador' && (
            <>
              <section className="ddt-section">
                <Text variant="label-sm" className="ddt-section-title">Vínculo com Intermediador</Text>
                <div className="ddt-info-grid">
                  <InfoRow
                    label="Intermediador"
                    value={
                      <div className="ddt-info-stack">
                        <Text variant="label-sm">{terminal.payfac}</Text>
                        <Text variant="body-xs" className="ddt-sublabel">{terminal.payfacId}</Text>
                      </div>
                    }
                  />
                  {terminal.vinculationStartDate && (
                    <InfoRow label="Data que foi vinculado" value={<Text variant="label-sm">{terminal.vinculationStartDate}</Text>} />
                  )}
                </div>
              </section>

              <ChecklistSection onConfirm={handleOpenConfirm} />
            </>
          )}

          {/* ═══════════════════════════════════════════════════════
              ALOCADA — tabela de vínculo atual
          ══════════════════════════════════════════════════════════ */}
          {status === 'Alocada Lojista' && (
            <>
              <section className="ddt-section">
                <Text variant="label-sm" className="ddt-section-title">Vínculo atual</Text>
                <div className="ddt-info-grid">
                  <InfoRow
                    label="Intermediador"
                    value={
                      <div className="ddt-info-stack">
                        <Text variant="label-sm">{terminal.payfac}</Text>
                        <Text variant="body-xs" className="ddt-sublabel">{terminal.payfacId}</Text>
                      </div>
                    }
                  />
                  <InfoRow
                    label="Lojista"
                    value={
                      <div className="ddt-info-stack">
                        <Text variant="label-sm">{terminal.seller}</Text>
                        <Text variant="body-xs" className="ddt-sublabel">{terminal.sellerId}</Text>
                      </div>
                    }
                  />
                  <InfoRow label="Vinculado em" value={<Text variant="label-sm">{terminal.vinculationDate}</Text>} />
                  <InfoRow label="Última transação" value={<Text variant="label-sm">{terminal.lastTransaction}</Text>} />
                </div>
              </section>

              <ChecklistSection onConfirm={handleOpenConfirm} />
            </>
          )}

          {/* ═══════════════════════════════════════════════════════
              MANUTENÇÃO — motivo da falha
          ══════════════════════════════════════════════════════════ */}
          {status === 'Manutenção' && terminal.failureReason && (
            <section className="ddt-section">
              <Text variant="label-sm" className="ddt-section-title">Motivo da falha</Text>
              <div className="ddt-failure-reason">
                <Text variant="body-sm">{terminal.failureReason}</Text>
              </div>
            </section>
          )}

        </div>
      </Drawer>

      {/* ── Modal de confirmação ─────────────────────────────────────────── */}
      {copy && confirmAction !== 'mudarStatus' && (
        <Modal
          isOpen={confirmAction !== null}
          title={copy.title}
          onClose={handleCloseModal}
          size="sm"
          footer={{
            primaryButton: {
              children: copy.primaryLabel,
              onClick: handleConfirm,
              disabled: !canConfirm,
            },
            secondaryButton: {
              children: 'Cancelar',
              variant: 'outline',
              onClick: handleCloseModal,
            },
            buttonLayout: 'fill',
          }}
        >
          <div className="ddt-modal-content">
            <p className="ddt-modal-desc">{copy.description(terminal.id)}</p>
            {(confirmAction === 'falha' || confirmAction === 'manutencao') && (
              <Input
                type="textarea"
                label="Motivo"
                placeholder="Descreva o motivo..."
                value={failureReason}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setFailureReason(e.target.value)
                }
              />
            )}
          </div>
        </Modal>
      )}

      {/* ── Modal mudar status — passo 1: seleção ───────────────────────── */}
      {confirmAction === 'mudarStatus' && mudarStatusStep === 1 && (
        <Modal
          isOpen
          title="Mudar status do terminal"
          onClose={handleCloseModal}
          size="sm"
          footer={{
            primaryButton: {
              children: 'Avançar',
              onClick: handleMudarStatusNext,
              disabled: targetStatus === '',
            },
            secondaryButton: {
              children: 'Cancelar',
              variant: 'outline',
              onClick: handleCloseModal,
            },
            buttonLayout: 'fill',
          }}
        >
          <div className="ddt-modal-content">
            <p className="ddt-modal-desc">Selecione o novo status para o terminal {terminal.id}.</p>
            <Select
              label="Novo status"
              placeholder="Selecione o status"
              hidePrefixIcon
              options={[
                { value: 'Estoque Barte', label: 'Estoque Barte' },
                { value: 'Teste', label: 'Teste' },
                { value: 'Desativada', label: 'Desativada' },
              ]}
              value={targetStatus}
              onChange={(val: string) => setTargetStatus(val as TerminalStatus)}
            />
          </div>
        </Modal>
      )}

      {/* ── Modal mudar status — passo 2: confirmação ───────────────────── */}
      {confirmAction === 'mudarStatus' && mudarStatusStep === 2 && (
        <Modal
          isOpen
          title="Confirmar mudança de status"
          onClose={handleCloseModal}
          size="sm"
          footer={{
            primaryButton: {
              children: 'Confirmar mudança',
              onClick: handleConfirm,
            },
            secondaryButton: {
              children: 'Voltar',
              variant: 'outline',
              onClick: handleMudarStatusBack,
            },
            buttonLayout: 'fill',
          }}
        >
          <div className="ddt-modal-content">
            <p className="ddt-modal-desc">
              Você está alterando o status do terminal <strong>{terminal.id}</strong> de{' '}
              <strong>Manutenção</strong> para <strong>{targetStatus}</strong>. Essa ação não pode ser desfeita.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}
